import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Dialog, DialogContent, IconButton, makeStyles, Tooltip } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/theme-xcode'
import SydButton from '../../../../../../commonDesign/Button'

const DIFF_MARKER_STYLES = `
  .ace_editor .ace_marker-layer .ddp-diff-added {
    background-color: rgba(34, 197, 94, 0.18);
    position: absolute;
  }
  .ace_editor .ace_marker-layer .ddp-diff-removed {
    background-color: rgba(239, 68, 68, 0.18);
    position: absolute;
  }
`

if (typeof document !== 'undefined' && !document.getElementById('ddp-versioned-diff-styles')) {
  const style = document.createElement('style')
  style.id = 'ddp-versioned-diff-styles'
  style.textContent = DIFF_MARKER_STYLES
  document.head.appendChild(style)
}

const useStyles = makeStyles(theme => ({
  paper: {
    height: '85vh',
    maxHeight: '85vh',
    margin: theme.spacing(2),
    overflow: 'hidden'
  },
  content: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  header: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    gap: theme.spacing(1.5)
  },
  title: {
    fontSize: 'var(--text-size-md)',
    fontWeight: 600,
    color: 'var(--color-neutral-800)'
  },
  subtitle: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-500)'
  },
  spacer: { flex: 1 },
  iconBtn: {
    padding: 4,
    color: 'var(--color-neutral-400)',
    '&:hover': { color: 'var(--color-neutral-700)' }
  },
  body: {
    flex: 1,
    display: 'flex',
    minHeight: 0,
    overflow: 'hidden'
  },
  pane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
    '& + &': { borderLeft: '1px solid var(--color-neutral-200)' }
  },
  paneHeader: {
    flexShrink: 0,
    padding: theme.spacing(0.75, 1.5),
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: 'var(--color-neutral-500)',
    backgroundColor: 'white',
    borderBottom: '1px solid var(--color-neutral-200)',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75)
  },
  removedLabel: { color: 'var(--color-danger-600)' },
  addedLabel: { color: 'var(--color-success-700)' },
  diffStat: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '11px',
    fontWeight: 700,
    padding: '1px 6px',
    borderRadius: 'var(--border-radius-sm)'
  },
  diffStatRemoved: {
    color: 'var(--color-danger-700)',
    backgroundColor: 'var(--color-danger-050, #fef2f2)'
  },
  diffStatAdded: {
    color: 'var(--color-success-700)',
    backgroundColor: 'var(--color-success-050, #f0fdf4)'
  },
  editor: { flex: 1, minHeight: 0 }
}))

function computeLineDiff (oldText, newText) {
  const a = (oldText || '').split('\n')
  const b = (newText || '').split('\n')
  const m = a.length
  const n = b.length
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) dp[i][j] = dp[i - 1][j - 1] + 1
      else dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1])
    }
  }
  const removed = []
  const added = []
  let i = m
  let j = n
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && a[i - 1] === b[j - 1]) {
      i--
      j--
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      added.push(j - 1)
      j--
    } else {
      removed.push(i - 1)
      i--
    }
  }
  return { removedLines: removed, addedLines: added }
}

function applyMarkers (editor, lines, className, markerIdsRef) {
  if (!editor) return
  const session = editor.getSession()
  for (const id of markerIdsRef.current) session.removeMarker(id)
  markerIdsRef.current = []
  if (!lines.length) return
  // ace-builds Range constructor; reach it via the live editor selection
  const Range = editor.getSelection().getRange().constructor
  for (const line of lines) {
    const range = new Range(line, 0, line, Number.MAX_VALUE)
    markerIdsRef.current.push(session.addMarker(range, className, 'fullLine', false))
  }
}

function ProposalDiffModal ({ open, currentText, proposedText, onAccept, onReject }) {
  const classes = useStyles()
  const currentEditorRef = useRef(null)
  const proposedEditorRef = useRef(null)
  const currentMarkerIds = useRef([])
  const proposedMarkerIds = useRef([])
  // We need to know when both Ace instances are mounted before painting markers.
  // The portaled Dialog content mounts after our render, so a plain useEffect
  // runs before the editor refs are populated.
  const [bothReady, setBothReady] = useState(false)
  const readyMaskRef = useRef(0)

  const { removedLines, addedLines } = useMemo(
    () => computeLineDiff(currentText || '', proposedText || ''),
    [currentText, proposedText]
  )

  const handleCurrentLoad = useCallback((editor) => {
    currentEditorRef.current = editor
    readyMaskRef.current |= 1
    if (readyMaskRef.current === 3) setBothReady(true)
  }, [])

  const handleProposedLoad = useCallback((editor) => {
    proposedEditorRef.current = editor
    readyMaskRef.current |= 2
    if (readyMaskRef.current === 3) setBothReady(true)
  }, [])

  useEffect(() => {
    if (!open) {
      readyMaskRef.current = 0
      setBothReady(false)
      currentEditorRef.current = null
      proposedEditorRef.current = null
      currentMarkerIds.current = []
      proposedMarkerIds.current = []
    }
  }, [open])

  useEffect(() => {
    if (!open || !bothReady) return
    applyMarkers(currentEditorRef.current, removedLines, 'ddp-diff-removed', currentMarkerIds)
    applyMarkers(proposedEditorRef.current, addedLines, 'ddp-diff-added', proposedMarkerIds)
  }, [open, bothReady, removedLines, addedLines])

  return (
    <Dialog
      open={open}
      onClose={onReject}
      maxWidth='lg'
      fullWidth
      classes={{ paper: classes.paper }}
    >
      <DialogContent className={classes.content}>
        <div className={classes.header}>
          <span className={classes.title}>Proposed pipeline change</span>
          <span className={classes.subtitle}>Review side-by-side, then accept or reject</span>
          <div className={classes.spacer} />
          <SydButton size='sm' variant='secondary' icon='close' onClick={onReject}>Reject</SydButton>
          <SydButton size='sm' variant='primary' icon='check' onClick={onAccept}>Accept</SydButton>
          <Tooltip title='Close' arrow>
            <IconButton className={classes.iconBtn} size='small' onClick={onReject}>
              <CloseIcon style={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.body}>
          <div className={classes.pane}>
            <div className={classes.paneHeader}>
              <span className={classes.removedLabel}>Current</span>
              {removedLines.length > 0 && (
                <span className={`${classes.diffStat} ${classes.diffStatRemoved}`}>
                  −{removedLines.length}
                </span>
              )}
            </div>
            <AceEditor
              className={classes.editor}
              mode='json'
              theme='xcode'
              name='ddp-proposal-current'
              readOnly
              value={currentText || ''}
              tabSize={2}
              width='100%'
              height='100%'
              showPrintMargin={false}
              setOptions={{ useWorker: false }}
              editorProps={{ $blockScroll: true }}
              onLoad={handleCurrentLoad}
            />
          </div>
          <div className={classes.pane}>
            <div className={classes.paneHeader}>
              <span className={classes.addedLabel}>Proposed</span>
              {addedLines.length > 0 && (
                <span className={`${classes.diffStat} ${classes.diffStatAdded}`}>
                  +{addedLines.length}
                </span>
              )}
            </div>
            <AceEditor
              className={classes.editor}
              mode='json'
              theme='xcode'
              name='ddp-proposal-proposed'
              readOnly
              value={proposedText || ''}
              tabSize={2}
              width='100%'
              height='100%'
              showPrintMargin={false}
              setOptions={{ useWorker: false }}
              editorProps={{ $blockScroll: true }}
              onLoad={handleProposedLoad}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

ProposalDiffModal.propTypes = {
  open: PropTypes.bool.isRequired,
  currentText: PropTypes.string,
  proposedText: PropTypes.string,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired
}

export default ProposalDiffModal
